import React, { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RicercaFornitore } from "@/components/RicercaAsync";

function EditDialog({ open, title, testid, onClose, onSave, saving, children }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">{children}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={`${testid}-annulla`}>Annulla</Button>
          <Button onClick={onSave} disabled={saving} data-testid={`${testid}-salva`}>{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ModificaFattura({ riga, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (riga)
      setForm({
        fornitore_id: riga.fornitore_id || "",
        numero_fattura: riga.numero_fattura || "",
        data: (riga.data || "").slice(0, 10),
        quantita: riga.quantita != null ? String(riga.quantita) : "",
        unita: riga.unita || "",
        costo_unitario: riga.costo_unitario != null ? String(riga.costo_unitario) : "",
        costo_totale: String(riga.costo_totale ?? ""),
        descrizione: riga.descrizione || "",
      });
  }, [riga]);
  const mut = useMutation({
    mutationFn: async (p) => (await api.put(`/materiali/${riga.id}`, p)).data,
    onSuccess: () => { toast.success("Acquisto aggiornato"); onSaved(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const q = form ? Number(form.quantita) : 0, cu = form ? Number(form.costo_unitario) : 0;
  const auto = form && form.quantita !== "" && form.costo_unitario !== "" && q > 0 ? Math.round(q * cu * 100) / 100 : null;
  const salva = () => {
    if (!form.descrizione) return toast.error("Inserisci la descrizione");
    const haQta = form.quantita !== "" && q > 0;
    mut.mutate({
      fornitore_id: form.fornitore_id, numero_fattura: form.numero_fattura, data: form.data, descrizione: form.descrizione,
      quantita: haQta ? q : null, unita: haQta ? form.unita : "", costo_unitario: haQta && form.costo_unitario !== "" ? cu : null,
      costo_totale: auto != null ? auto : Number(form.costo_totale) || 0,
    });
  };
  return (
    <EditDialog open={!!riga} title="Modifica acquisto" testid="modifica-fattura-dialog" onClose={onClose} onSave={salva} saving={mut.isPending}>
      {form && (
        <>
          <div className="space-y-1.5 col-span-2">
            <Label>Fornitore</Label>
            <RicercaFornitore value={form.fornitore_id} onChange={(v) => set("fornitore_id")(v)} testid="modifica-fattura-fornitore" />
          </div>
          <div className="space-y-1.5">
            <Label>N. fattura</Label>
            <Input value={form.numero_fattura} onChange={(e) => set("numero_fattura")(e.target.value)} data-testid="modifica-fattura-numero" />
          </div>
          <div className="space-y-1.5">
            <Label>Data</Label>
            <Input type="date" value={form.data} onChange={(e) => set("data")(e.target.value)} data-testid="modifica-fattura-data" />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Descrizione materiale</Label>
            <Input value={form.descrizione} onChange={(e) => set("descrizione")(e.target.value)} data-testid="modifica-fattura-descrizione" />
          </div>
          <div className="space-y-1.5">
            <Label>Quantità</Label>
            <Input type="number" min="0" step="0.01" value={form.quantita} onChange={(e) => set("quantita")(e.target.value)} data-testid="modifica-fattura-quantita" />
          </div>
          <div className="space-y-1.5">
            <Label>Unità</Label>
            <Select value={form.unita || "__nessuna__"} onValueChange={(v) => set("unita")(v === "__nessuna__" ? "" : v)}>
              <SelectTrigger data-testid="modifica-fattura-unita"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__nessuna__">—</SelectItem>
                {["pz", "kg", "t", "m", "m²", "m³", "l", "h", "conf", "barra", "lastra", "rotolo"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Costo unitario (€)</Label>
            <Input type="number" min="0" step="0.0001" value={form.costo_unitario} onChange={(e) => set("costo_unitario")(e.target.value)} data-testid="modifica-fattura-costo-unitario" />
          </div>
          <div className="space-y-1.5">
            <Label>Importo totale (€) {auto != null && <span className="text-xs text-primary">calcolato</span>}</Label>
            <Input type="number" step="0.01" min="0" value={auto != null ? auto : form.costo_totale} readOnly={auto != null} className={auto != null ? "bg-orange-50 font-mono" : ""} onChange={(e) => set("costo_totale")(e.target.value)} data-testid="modifica-fattura-importo" />
          </div>
        </>
      )}
    </EditDialog>
  );
}

export function ModificaTrasferta({ riga, dipendenti, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (riga)
      setForm({
        dipendente_id: riga.dipendente_id || "",
        data: (riga.data || "").slice(0, 10),
        notti: String(riga.notti ?? ""),
        indennita_notte: String(riga.indennita_notte ?? ""),
        descrizione: riga.descrizione || "",
      });
  }, [riga]);
  const mut = useMutation({
    mutationFn: async (p) => (await api.put(`/trasferte/${riga.id}`, p)).data,
    onSuccess: () => { toast.success("Trasferta aggiornata"); onSaved(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const salva = () => {
    if (!form.dipendente_id || !(Number(form.notti) > 0)) return toast.error("Compila dipendente e notti");
    mut.mutate({
      dipendente_id: form.dipendente_id,
      data: form.data || undefined,
      notti: Number(form.notti),
      indennita_notte: form.indennita_notte === "" ? undefined : Number(form.indennita_notte),
      descrizione: form.descrizione,
    });
  };
  return (
    <EditDialog open={!!riga} title="Modifica trasferta" testid="modifica-trasferta-dialog" onClose={onClose} onSave={salva} saving={mut.isPending}>
      {form && (
        <>
          <div className="space-y-1.5 col-span-2">
            <Label>Dipendente</Label>
            <Select value={form.dipendente_id} onValueChange={set("dipendente_id")}>
              <SelectTrigger data-testid="modifica-trasferta-dipendente"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{dipendenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Data</Label>
            <Input type="date" value={form.data} onChange={(e) => set("data")(e.target.value)} data-testid="modifica-trasferta-data" />
          </div>
          <div className="space-y-1.5">
            <Label>Notti</Label>
            <Input type="number" min="1" step="1" value={form.notti} onChange={(e) => set("notti")(e.target.value)} data-testid="modifica-trasferta-notti" />
          </div>
          <div className="space-y-1.5">
            <Label>€/notte</Label>
            <Input type="number" min="0" step="0.01" value={form.indennita_notte} onChange={(e) => set("indennita_notte")(e.target.value)} data-testid="modifica-trasferta-indennita" />
          </div>
          <div className="space-y-1.5">
            <Label>Descrizione</Label>
            <Input value={form.descrizione} onChange={(e) => set("descrizione")(e.target.value)} data-testid="modifica-trasferta-descrizione" />
          </div>
        </>
      )}
    </EditDialog>
  );
}
